import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAttachmentsPermissions } from '../../../../modules/attachments/Attachments.permissions';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { ACDLError } from '../../../models/error.model';
import { Home } from '../../../routes/config/Home';
import { toMBytes } from '../../datasets/helpers/image.helpers';
import { IAttachmentsPreviewService } from '../attachmentsPreview.service';
import { AttachmentDetailsPreviewHeader } from '../components/AttachmentDetailsPreviewHeader';

export const AttachmentsPreviewHeaderContainer = observer((): ReactElement => {
  const history = useHistory();
  const previewService = useInjection(IAttachmentsPreviewService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const permissions = useAttachmentsPermissions();
  const canView = permissions.canViewAttachments();

  const [total, setTotal] = useState(0);
  const [name, setName] = useState('');
  const [current, setCurrent] = useState(0);
  const [previousId, setPreviousId] = useState('');
  const [nextId, setNextId] = useState('');
  const [imageSize, setImageSize] = useState(0);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [createdDate, setCreatedDate] = useState('');
  const [id, setId] = useState('');
  const params = useParams<{ workspaceId: string; attachmentId: string; parentId?: string }>();

  useEffect(() => {
    void showImageAsync();
  }, [params.attachmentId]);

  const showImageAsync = async (): Promise<void> => {
    if (!canView.allowed) {
      history.push(Home.Attachments.List.Folder.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id, attachmentId: '' }));
      return;
    }

    const data = previewService.data;
    const previewPaging = data.previewPaging;
    const attachmentId = params.attachmentId.toLowerCase();
    const parentId = params.parentId?.toLowerCase() || '';
    const workspaceId = params.workspaceId.toLowerCase() || '';

    data.isPreviewImageLoading = true;
    data.lowQualityImagePreviewUrl = '';
    data.imagePreviewUrl = '';

    const [paging, image] = await Promise.all([
      previewService.getPreviewPagingAsync(workspaceId, parentId, previewPaging.id === parentId ? previewPaging.checkDate : ''),
      previewService.getImageInfoAsync(attachmentId),
    ]);

    if (image instanceof ACDLError || paging instanceof ACDLError) {
      return;
    }

    const oldPaging = { ...previewPaging };
    if (paging.wasChanged) {
      previewPaging.id = paging.id || '';
      previewPaging.checkDate = paging.checkDate;
      previewPaging.items = paging.items;
    }

    let currentItemIndex = 1;
    const currentItem = previewPaging.items.find((x) => x.imageId === image.id);
    if (currentItem !== undefined) {
      currentItemIndex = currentItem.index;
    } else {
      const previousItem = oldPaging.items.find((x) => x.imageId === image.id);
      if (previousItem !== undefined) {
        const newImage = previewPaging.items.find((x) => x.index === previousItem.index);
        if (newImage !== undefined) {
          history.push(Home.Attachments.List.Preview.withParams({ workspaceId, attachmentId: newImage.imageId }));
          return;
        }
        if (previewPaging.items.length > 0) {
          const newImage = previewPaging.items[previewPaging.items.length - 1];
          history.push(Home.Attachments.List.Preview.withParams({ workspaceId, attachmentId: newImage.imageId }));
          return;
        }
      }
      history.push(Home.Attachments.Path);
      return;
    }

    const next = previewPaging.items.find((x) => x.index === currentItemIndex + 1);
    const previous = previewPaging.items.find((x) => x.index === currentItemIndex - 1);

    await previewService.showImageAsync(attachmentId, image.size);

    setTotal(previewPaging.items.length);
    setName(image.name);
    setCurrent(currentItemIndex);
    setPreviousId(previous ? previous.imageId : '');
    setNextId(next ? next.imageId : '');
    setImageSize(image.size);
    setImageHeight(image.height);
    setImageWidth(image.width);
    setCreatedDate(image.createdDate);
    setId(image.id);
  };

  const data = previewService.data;
  const parentId = params.parentId || '';

  return (
    <AttachmentDetailsPreviewHeader
      channels={data.imageAttributes.channels || 0}
      closeButtonLink={Home.Attachments.List.Folder.withParams({ workspaceId: params.workspaceId, attachmentId: parentId })}
      createdDate={createdDate}
      current={current}
      downloadSpeed={data.imageAttributes.downloadSpeed}
      id={id}
      imageHeight={imageHeight}
      imageSize={toMBytes(imageSize)}
      imageWidth={imageWidth}
      isCached={data.imageAttributes.isCached || false}
      isHighResImageLoading={data.imagePreviewUrl === ''}
      isLoading={data.isPreviewImageLoading}
      name={name}
      nextLink={Home.Attachments.List.Preview.withParams({ parentId, workspaceId: params.workspaceId, attachmentId: nextId })}
      parentId={parentId}
      previousLink={Home.Attachments.List.Preview.withParams({
        parentId,
        workspaceId: params.workspaceId,
        attachmentId: previousId,
      })}
      total={total}
    />
  );
});
